import Vue from "vue";
import VueRouter from "vue-router";
import store from "@/store";
import {
  LANDING_ROUTES,
  SETTINGS_ROUTE_SUBTABS,
  tabViewCode,
} from "@/app-js/access-codes";
import { getAuth } from "@/app-js/authStorage";
import { noteAction } from "@/app-js/session";
import { homeOf as partnerHome, readSession as readPartnerSession } from "@/app-js/partner-portal/session";

Vue.use(VueRouter);

const routes = [
  {
    path: "/login/:code?",
    name: "login",
    meta: { requiresAuth: false, title: "Login", show_back_btn: false },
    component: () =>
      import(/* webpackChunkName: "Login" */ "@/views/LoginPage/Login.vue"),
  },
  {
    path: "/",
    redirect:"/login",
    name: "home",
    component: () =>
      import(
        /* webpackChunkName: "BaseLayout" */ "@/components/layouts/Base.vue"
      ),
    beforeEnter: (to, from, next) => {
      guard(to, from, next);
    },
    children: [
      // The board views. Each is its own route so it can be a sidebar entry, but
      // they all carry `meta.tab_title: "Dashboard"` and therefore ride the
      // existing DASHBOARD permission group instead of each needing a grant of
      // its own. `meta.title` is what the app bar shows; `meta.dash_tab` picks
      // which view the shared Dashboard shell renders.
      {
        path: 'dashboard',
        name: 'dashboard',
        redirect: { name: 'dashboard-funnel' },
      },
      {
        path: 'dashboard/funnel',
        name: 'dashboard-funnel',
        // `dash_filters: "drawer"` puts this view's filters in the right-hand
        // sidebar (FunnelFilterDrawer) instead of the shell's top bar.
        // Its figures are the Leads and LOS pages' tab badges, so it offers those
        // pages' own date filters, starts with no period as they do, and keeps its
        // choices apart from the money boards, which start on the current year.
        meta: {
          requiresAuth: true, title: "Lead & App Journey", tab_title: "Dashboard", dash_tab: "FunnelTab",
          dash_filters: "drawer", permission: "dashboard.funnel.view",
          dash_filter_sections: ["period", "journey_dates", "product", "location", "dealer", "team", "outcome"],
          dash_filter_store: "dash_journey_filters",
          dash_default_years: [],
        },
        component: () => import( /* webpackChunkName: "Dashboard" */ "@/views/Dashboard/index.vue")
      },
      {
        path: 'dashboard/disbursement',
        name: 'dashboard-disbursement',
        // The only board with a turnaround to filter on, so it is the only one
        // that shows that section.
        meta: {
          requiresAuth: true, title: "Disbursement Journey", tab_title: "Dashboard",
          dash_tab: "DisbursementTab", dash_filters: "drawer", permission: "dashboard.disbursement.view",
          dash_filter_sections: ["period", "product", "location", "dealer", "team", "outcome", "tat"],
        },
        component: () => import( /* webpackChunkName: "Dashboard" */ "@/views/Dashboard/index.vue")
      },
      {
        // The book: stock as on today. Period only narrows the closure counts.
        path: 'dashboard/portfolio',
        name: 'dashboard-portfolio',
        meta: {
          requiresAuth: true, title: "Loan Book Journey", tab_title: "Dashboard",
          dash_tab: "BooksTab", dash_filters: "drawer", permission: "dashboard.books.view",
          dash_filter_sections: ["period", "product", "location", "book"],
        },
        component: () => import( /* webpackChunkName: "Dashboard" */ "@/views/Dashboard/index.vue")
      },
      {
        // Collections: flow over the window, led by due against collected.
        path: 'dashboard/collections',
        name: 'dashboard-collections',
        meta: {
          requiresAuth: true, title: "Collections Journey", tab_title: "Dashboard",
          dash_tab: "CollectionsTab", dash_filters: "drawer", permission: "dashboard.collections.view",
          dash_filter_sections: ["period", "agreement_date", "product", "location", "book"],
        },
        component: () => import( /* webpackChunkName: "Dashboard" */ "@/views/Dashboard/index.vue")
      },
      {
        // The single-page dashboard from before the boards, kept as a fifth
        // board. It is its own page, not a view of the shared shell; `dash_tab`
        // is only set so the sidebar lights its entry and the top bar shows the
        // "Dashboard ›" crumb like the other boards.
        path: 'dashboard/old',
        name: 'dashboard-old',
        meta: {
          requiresAuth: true, title: "Old Dashboard", tab_title: "Dashboard",
          dash_tab: "OldDashboard", permission: "dashboard.old.view",
        },
        component: () => import( /* webpackChunkName: "OldDashboard" */ "@/views/Dashboard/OldDashboard.vue")
      },
      {
        path: "team",
        name: "team",
        meta: {
          requiresAuth: true,
          title: "Team",
          show_back_btn: false,
          tab_title: "Team",
        },
        component: () =>
          import(/* webpackChunkName: "Team" */ "@/views/Admin/team/index.vue"),
      },
      {
        path: "team/details/:id",
        name: "team-details",
        meta: {
          requiresAuth: true,
          title: "Team Member Details",
          show_back_btn: true,
          tab_title: "Team",
        },
        component: () =>
          import(
            /* webpackChunkName: "Team" */ "@/views/Admin/team/ViewMemberDetails.vue"
          ),
      },
      {
        path: "team/permission-groups",
        name: "permission-groups",
        meta: {
          requiresAuth: true,
          title: "Permission Groups",
          show_back_btn: true,
          tab_title: "Team",
          permission: "team.permissions",
        },
        component: () =>
          import(
            /* webpackChunkName: "Team" */ "@/views/Admin/team/permission-groups/index.vue"
          ),
      },
      {
        path: "team/permission-groups/new",
        name: "permission-group-new",
        meta: {
          requiresAuth: true,
          title: "New Permission Group",
          show_back_btn: true,
          tab_title: "Team",
          permission: "team.permissions",
        },
        component: () =>
          import(
            /* webpackChunkName: "Team" */ "@/views/Admin/team/permission-groups/GroupEditor.vue"
          ),
      },
      {
        path: "team/permission-groups/:id",
        name: "permission-group-edit",
        meta: {
          requiresAuth: true,
          title: "Edit Permission Group",
          show_back_btn: true,
          tab_title: "Team",
          permission: "team.permissions",
        },
        component: () =>
          import(
            /* webpackChunkName: "Team" */ "@/views/Admin/team/permission-groups/GroupEditor.vue"
          ),
      },
      {
        path: "no-access",
        name: "no-access",
        meta: {
          requiresAuth: true,
          title: "No access",
          show_back_btn: false,
          skip_tab_permission: true,
        },
        component: () =>
          import(/* webpackChunkName: "NoAccess" */ "@/views/NoAccess.vue"),
      },
      {
        path: "team/add",
        name: "add-team",
        meta: {
          requiresAuth: true,
          title: "Add Team Member",
          show_back_btn: true,
          tab_title: "Team",
        },
        component: () =>
          import(
            /* webpackChunkName: "Team" */ "@/views/Admin/team/AddTeamMember.vue"
          ),
      },
      {
        path: "loan-scheme",
        name: "loan-scheme",
        meta: {
          requiresAuth: true,
          title: "Loan Schema",
          show_back_btn: false,
          tab_title: "Loan Schema",
        },
        component: () =>
          import(
            /* webpackChunkName: "LoanSchema" */ "@/views/Admin/LoanScheme/index.vue"
          ),
      },
      {
        path: "loan-scheme/details/:id",
        name: "loan-scheme-details",
        meta: {
          requiresAuth: true,
          title: "Loan Schema",
          show_back_btn: true,
          tab_title: "Loan Schema",
        },
        component: () =>
          import(
            /* webpackChunkName: "LoanSchema" */ "@/views/Admin/LoanScheme/ViewLoanSchemeDetails.vue"
          ),
      },
      {
        path: "loan-scheme/add",
        name: "add-loan-scheme",
        meta: {
          requiresAuth: true,
          title: "Loan Schema",
          show_back_btn: true,
          tab_title: "Loan Schema",
        },
        component: () =>
          import(
            /* webpackChunkName: "LoanSchema" */ "@/views/Admin/LoanScheme/AddLoanSchemeDetails.vue"
          ),
      },
      {
        path: "lead-list",
        name: "lead-list",
        meta: {
          requiresAuth: true,
          title: "LOS",
          show_back_btn: false,
          tab_title: "LOS",
        },
        component: () =>
          import(/* webpackChunkName: "LOS" */ "@/views/LOS/index.vue"),
      },
      {
        path: "lead-list/details/:id",
        name: "lead-details",
        meta: {
          requiresAuth: true,
          title: "Lead Details",
          show_back_btn: true,
          tab_title: "LOS",
        },
        component: () =>
          import(/* webpackChunkName: "LOS" */ "@/views/LOS/LeadDetails.vue"),
      },
      // {
      //   path: "lead-list/add",
      //   name: "add-lead-list",
      //   meta: {
      //     requiresAuth: true,
      //     title: "Create Lead",
      //     show_back_btn: true,
      //     tab_title: "LOS",
      //   },
      //   component: () =>
      //     import(/* webpackChunkName: "team" */ "@/views/LOS/CreateLead.vue"),
      // },
      {
        path: "lead-list/add/:loan_type_id/:loan_type/:lead_creation?",
        name: "add-lead-list",
        meta: {
          requiresAuth: true,
          title: "Create",
          show_back_btn: true,
          tab_title: "LOS",
        },
        component: () =>
          import(/* webpackChunkName: "LOS" */ "@/views/LOS/createNewLead.vue"),
      },
      {
        path: "lms-list",
        meta: {
          requiresAuth: true,
          title: "LMS",
          show_back_btn: true,
          tab_title: "LMS",
        },
        component: () =>
          import(/* webpackChunkName: "LMS" */ "@/views/LMS/index.vue"),

        children: [
          {
            path: "/",
            name: "lms-List",
            meta: {
              requiresAuth: true,
              title: "LMS",
              show_back_btn: true,
              tab_title: "LMS",
            },
            component: () =>
              import(
                /* webpackChunkName: "LMS" */ "@/components/lms/LmsList.vue"
              ),
          },
          {
            path: "details/:id/:mobile",
            name: "lms-details",
            meta: {
              requiresAuth: true,
              title: "LMS Details",
              show_back_btn: true,
              tab_title: "LMS",
            },
            component: () =>
              import(
                /* webpackChunkName: "LMS" */ "@/views/LMS/LmsDetails.vue"
              ),
          },
        ],
      },
      // {
      //   path: "/lms-list/lms-details/:id/:mobile",
      //   name: "lms-details",
      //   meta: {
      //     requiresAuth: true,
      //     title: "LMS Details",
      //     show_back_btn: true,
      //     tab_title: "LMS",
      //   },
      //   component: () =>
      //     import(/* webpackChunkName: "team" */ "@/views/LMS/LmsDetails.vue"),
      // },

      {
        path: "nach-list",
        name: "nach-list",
        meta: {
          requiresAuth: true,
          title: "NACH",
          show_back_btn: false,
          tab_title: "NACH",
        },
        component: () =>
          import(/* webpackChunkName: "NACH" */ "@/views/NACH/index.vue"),
      },
      {
        path: "account-list",
        name: "account-list",
        meta: {
          requiresAuth: true,
          title: "Disbursement",
          show_back_btn: false,
          tab_title: "Disbursement",
        },
        component: () =>
          import(
            /* webpackChunkName: "Accounts" */ "@/views/Accounts/index.vue"
          ),
      },
      {
        path: "account-list/disbursement/:id/:status",
        name: "account_disbursement",
        meta: {
          requiresAuth: true,
          title: "Disbursement",
          show_back_btn: true,
          tab_title: "Disbursement",
        },
        component: () =>
          import(
            /* webpackChunkName: "Disbursement" */ "@/views/Accounts/Disbursement/Disbursement.vue"
          ),
      },
      {
        path: "collection-list",
        name: "collection-list",
        meta: {
          requiresAuth: true,
          title: "Collection",
          show_back_btn: false,
          tab_title: "Collection",
        },
        component: () =>
          import(
            /* webpackChunkName: "Collections" */ "@/views/Collections/index.vue"
          ),
      },
      {
        path: "collection-list/contacts/:id",
        name: "collection-contacts",
        meta: {
          requiresAuth: true,
          title: "Collection Contacts",
          show_back_btn: true,
          tab_title: "Collection",
        },
        component: () =>
          import(
            /* webpackChunkName: "Collections" */ "@/views/Collections/contacts/contacts.vue"
          ),
      },
      {
        path: "notice-management",
        name: "notice-management",
        meta: {
          requiresAuth: true,
          title: "Notice Management",
          show_back_btn: false,
          tab_title: "Notice Management",
        },
        component: () =>
          import(
            /*webpackChunkName: "NoticeManagement" */ "@/views/NoticeManagement/index.vue"
          ),
      },
      {
        path: "notice-management/details/:id",
        name: "notice_details",
        meta: {
          requiresAuth: true,
          title: "Notice Details",
          show_back_btn: true,
          tab_title: "Notice Management",
        },
        component: () =>
          import(
            /* webpackChunkName: "NoticeManagement" */ "@/views/NoticeManagement/ViewNotice.vue"
          ),
      },
      {
        path: "notice-management/editdetails/:id",
        name: "edit_details",
        meta: {
          requiresAuth: true,
          title: "Edit Notice",
          show_back_btn: true,
          tab_title: "Notice Management",
        },
        component: () =>
          import(
            /* webpackChunkName: "NoticeManagement" */ "@/views/NoticeManagement/EditNotice.vue"
          ),
      },
      {
        path: "session-history",
        name: "session_history",
        meta: {
          requiresAuth: true,
          title: "User Sessions",
          show_back_btn: false,
          tab_title: "User Sessions",
        },
        component: () =>
          import(
            /* webpackChunkName: "SessionHistory" */ "@/views/SessionHistory/sessionHistory.vue"
          ),
      },
      {
        path: "session-history/:id",
        name: "session_details",
        meta: {
          requiresAuth: true,
          title: "Session History",
          show_back_btn: true,
          tab_title: "User Sessions",
        },
        component: () =>
          import(
            /* webpackChunkName: "SessionHistory" */ "@/views/SessionHistory/MemberSessions.vue"
          ),
      },
      {
        // Your own history, from the profile menu: open to every teammate.
        path: "my-sessions",
        name: "my-session-history",
        meta: {
          requiresAuth: true,
          title: "My Session History",
          show_back_btn: true,
          skip_tab_permission: true,
        },
        component: () =>
          import(
            /* webpackChunkName: "SessionHistory" */ "@/views/SessionHistory/MemberSessions.vue"
          ),
      },
      {
        path: "ledger-panel",
        name: "ledger-panel",
        meta: {
          requiresAuth: true,
          title: "Accounts",
          show_back_btn: false,
          tab_title: "Accounts",
        },
        component: () =>
          import(/* webpackChunkName: "Ledger" */ "@/views/Ledger/index.vue"),
      },
      {
        path: "payments",
        name: "payments-panel",
        meta: {
          requiresAuth: true,
          title: "Payments",
          show_back_btn: false,
          tab_title: "Payments",
        },
        component: () =>
          import(
            /* webpackChunkName: "Payment" */ "@/views/Payments/index.vue"
          ),
      },
      {
        path: "settings",
        name: "settings",
        meta: {
          requiresAuth: true,
          title: "",
          show_back_btn: false,
          tab_title: "Settings",
        },
        // The sections rail is part of every Settings screen now, so there is
        // no separate landing page to show -- open the first section this
        // teammate may see (Feature Flags first, as before).
        redirect: () => ({ name: firstSettingsRoute() }),
      },
      {
        path: "settings/profile",
        name: "settings profile",
        meta: {
          requiresAuth: true,
          title: "",
          show_back_btn: false,
          tab_title: "Settings",
        },
        component: () =>
          import(
            /* webpackChunkName: "Settings" */ "@/views/Settings/OrganisationProfile.vue"
          ),
      },
      {
        path: "settings/calling",
        name: "settings calling",
        meta: {
          requiresAuth: true,
          title: "",
          show_back_btn: false,
          tab_title: "Settings",
        },
        component: () =>
          import(
            /* webpackChunkName: "Settings" */ "@/views/Settings/Calling.vue"
          ),
      },
      {
        path: "settings/flags",
        name: "settings flags",
        meta: {
          requiresAuth: true,
          title: "",
          show_back_btn: false,
          tab_title: "Settings",
        },
        component: () =>
          import(
            /* webpackChunkName: "Settings" */ "@/views/Settings/FeatureFlags.vue"
          ),
      },
      {
        path: "settings/activity",
        name: "settings activity",
        meta: {
          requiresAuth: true,
          title: "",
          show_back_btn: false,
          tab_title: "Settings",
        },
        component: () =>
          import(
            /* webpackChunkName: "Settings" */ "@/views/Settings/SettingsActivity.vue"
          ),
      },
      {
        path: "settings/reloan-rules",
        name: "settings reloan",
        meta: {
          requiresAuth: true,
          title: "",
          show_back_btn: false,
          tab_title: "Settings",
        },
        component: () =>
          import(
            /* webpackChunkName: "Settings" */ "@/views/Settings/ReloanRules.vue"
          ),
      },
      {
        path: "settings/noc-charges",
        name: "settings noc charges",
        meta: {
          requiresAuth: true,
          title: "",
          show_back_btn: false,
          tab_title: "Settings",
        },
        component: () =>
          import(
            /* webpackChunkName: "Settings" */ "@/views/Settings/NocCharges.vue"
          ),
      },
      {
        path: "settings/auto-approval",
        name: "settings auto approval",
        meta: {
          requiresAuth: true,
          title: "",
          show_back_btn: false,
          tab_title: "Settings",
        },
        component: () =>
          import(
            /* webpackChunkName: "Settings" */ "@/views/Settings/AutoApproval.vue"
          ),
      },
      {
        path: "settings/disbursement-accounts",
        name: "settings disbursement accounts",
        meta: {
          requiresAuth: true,
          title: "",
          show_back_btn: false,
          tab_title: "Settings",
        },
        component: () =>
          import(
            /* webpackChunkName: "Settings" */ "@/views/Settings/DisbursementAccounts.vue"
          ),
      },
      {
        path: "settings/auto-assignment",
        name: "settings auto assignment",
        meta: {
          requiresAuth: true,
          title: "",
          show_back_btn: false,
          tab_title: "Settings",
        },
        component: () =>
          import(
            /* webpackChunkName: "Settings" */ "@/views/Settings/AutoAssignment.vue"
          ),
      },
      {
        path: "settings/partner-offers",
        name: "settings partner offers",
        meta: {
          requiresAuth: true,
          title: "",
          show_back_btn: false,
          tab_title: "Settings",
        },
        component: () =>
          import(
            /* webpackChunkName: "Settings" */ "@/views/Settings/PartnerOffers.vue"
          ),
      },
      {
        path: "settings/app-banners",
        name: "settings banners",
        meta: {
          requiresAuth: true,
          title: "",
          show_back_btn: false,
          tab_title: "Settings",
        },
        component: () =>
          import(
            /* webpackChunkName: "Settings" */ "@/views/Settings/Banners.vue"
          ),
      },
      {
        // "verifiaction" was a typo in the path, so the correctly-spelled URL
        // 404'd to a blank page. In-app navigation never hit it because the
        // rail pushes by route NAME, which is why it went unnoticed. The old
        // spelling stays as an alias so any existing link keeps resolving; the
        // route name is left alone because two components reference it.
        path: "settings/verification-parameters",
        alias: "settings/verifiaction-parameters",
        name: "verifiaction parameters",
        meta: {
          requiresAuth: true,
          title: "",
          show_back_btn: false,
          tab_title: "Settings",
        },
        component: () =>
          import(/* webpackChunkName: "Settings" */ "@/views/Settings/VerificationParameters.vue"),

      },
      {
        path: "whats-new",
        name: "whats-new",
        meta: {
          requiresAuth: true,
          title: "What's New",
          show_back_btn: true,
          tab_title: "What's New",
          // Reachable from the profile menu by every signed-in staff member, so
          // it deliberately has no permission group of its own.
          skip_tab_permission: true,
        },
        component: () =>
          import(/* webpackChunkName: "WhatsNew" */ "@/views/WhatsNew/index.vue"),
      },
      {
        path: "delete-requests",
        name: "delete-requests",
        meta: {
          requiresAuth: true,
          title: "Delete Requests",
          show_back_btn: false,
          tab_title: "Delete Requests",
        },
        component: () =>
          import(
            /* webpackChunkName: "DeleteRequests" */ "@/views/Admin/deleteRequests/index.vue"
          ),
      },
      {
        path: "dealer",
        name: "dealer",
        meta: {
          requiresAuth: true,
          title: "Dealer",
          show_back_btn: false,
          tab_title: "Dealer",
        },
        component: () =>
          import(
            /* webpackChunkName: "Dealer" */ "@/views/Admin/dealer/index.vue"
          ),
      },
      {
        // dealer_id (the uid) is part of the PATH, not a bare param: Vue Router
        // drops non-path params on reload, so the details page was requesting
        // /get_or_create_dealer_doc/undefined/ after a refresh and the API 500'd.
        path: "dealer/details/:id/:dealer_id?",
        name: "dealer-details",
        meta: {
          requiresAuth: true,
          title: "Dealer Details",
          show_back_btn: true,
          tab_title: "Dealer",
        },
        component: () =>
          import(
            /* webpackChunkName: "Dealer" */ "@/views/Admin/dealer/ViewDealerDetails.vue"
          ),
      },
      {
        path: "dealer/add",
        name: "add-dealer",
        meta: {
          requiresAuth: true,
          title: "Add Dealer",
          show_back_btn: true,
          tab_title: "Dealer",
        },
        component: () =>
          import(
            /* webpackChunkName: "Dealer" */ "@/views/Admin/dealer/AddDealer.vue"
          ),
      },
      {
        path: "reports",
        name: "reports-panel",
        meta: {
          requiresAuth: true,
          title: "Reports",
          show_back_btn: false,
          tab_title: "Reports",
        },
        component: () =>
          import(/* webpackChunkName: "Reports" */ "@/views/Reports/index.vue"),
      },
      {
        path: "notifications",
        name: "notifications",
        meta: {
          requiresAuth: true,
          title: "Notifications",
          show_back_btn: false,
          tab_title: "Notifications",
        },
        component: () =>
          import(/* webpackChunkName: "Notifications" */ "@/views/Notifications/index.vue"),
      },
      {
        path: "reports/create-report-template",
        name: "create-report-template",
        meta: {
          requiresAuth: true,
          title: "Create Report Template",
          show_back_btn: true,
          tab_title: "Reports",
        },
        component: () =>
          import(
            /* webpackChunkName: "Reports" */ "@/views/Reports/CreateTemplate.vue"
          ),
      },
      {
        path: "reports/generate-report",
        name: "generate-report",
        meta: {
          requiresAuth: true,
          title: "Generate Report",
          show_back_btn: true,
          tab_title: "Reports",
        },
        component: () =>
          import(
            /* webpackChunkName: "Reports" */ "@/views/Reports/GenerateReport.vue"
          ),
      },
      {
        path: "agent-los",
        meta: {
          requiresAuth: true,
          title: "Leads",
          show_back_btn: false,
          tab_title: "Leads",
        },
        component: () =>
          import(/* webpackChunkName: "Reports" */ "@/views/Agent/index.vue"),
        children: [
          {
            path: "/",
            name: "agent-los",
            meta: {
              requiresAuth: true,
              title: "Leads",
              show_back_btn: false,
              tab_title: "Leads",
            },
            component: () =>
              import(
                /* webpackChunkName: "LMS" */ "@/components/agent/AgentList.vue"
              ),
          },
          // {
          //   path: "create",
          //   name: "create-lead",
          //   meta: {
          //     requiresAuth: true,
          //     title: "Create Lead",
          //     show_back_btn: true,
          //     tab_title: "Agent LOS",
          //   },
          //   component: () =>
          //     import(
          //       /* webpackChunkName: "LMS" */ "@/components/agent/CheckLeadForm.vue"
          //     ),
          // },
          {
            path: "create-lead/:loan_type_id/:loan_type/:lead_creation?",
            name: "agent-create-lead",
            meta: {
              requiresAuth: true,
              title: "Create",
              show_back_btn: true,
              tab_title: "Leads",
            },
            component: () =>
              import(
                /* webpackChunkName: "LOS" */ "@/views/LOS/createNewLead.vue"
              ),
          },
        ],
      },
    ],
  },
  // Partner Portal -- each lender's own front door, separate from the staff
  // console: /partner/<nbfc> is that lender's sign-in, in its name and logo, and
  // only a real, live lender code opens it (anything else -- /partner,
  // /partner/login -- says the link is not valid). A signed-in partner lives at
  // /partner/<nbfc>/<own-dsa> and nowhere else (partnerLock below; the server
  // re-checks on every request). /partner/view opens an admin's view-only pass.
  {
    path: "/partner/view",
    name: "partner-view",
    meta: { requiresAuth: false, partnerPublic: true, title: "Partner view" },
    component: () =>
      import(/* webpackChunkName: "PartnerPortal" */ "@/views/PartnerPortal/PartnerView.vue"),
  },
  {
    path: "/partner",
    name: "partner-nolender",
    meta: { requiresAuth: false, partnerPublic: true, title: "Partner sign in" },
    component: () =>
      import(/* webpackChunkName: "PartnerPortal" */ "@/views/PartnerPortal/PartnerLanding.vue"),
  },
  {
    path: "/partner/:nbfc",
    name: "partner-login",
    meta: { requiresAuth: false, partnerPublic: true, title: "Partner sign in" },
    component: () =>
      import(/* webpackChunkName: "PartnerPortal" */ "@/views/PartnerPortal/PartnerLanding.vue"),
  },
  { path: "/partner/:nbfc/login", redirect: { name: "partner-login" } },
  { path: "/partner/:nbfc/signup", redirect: { name: "partner-login" } },
  {
    path: "/partner/:nbfc/:dsa",
    name: "partner-home",
    meta: { requiresAuth: false, partnerPrivate: true, title: "Dashboard" },
    component: () =>
      import(/* webpackChunkName: "PartnerPortal" */ "@/views/PartnerPortal/PartnerHome.vue"),
  },
  {
    path: "/partner/:nbfc/:dsa/reports",
    name: "partner-reports",
    meta: { requiresAuth: false, partnerPrivate: true, title: "Reports" },
    component: () =>
      import(/* webpackChunkName: "PartnerPortal" */ "@/views/PartnerPortal/PartnerHome.vue"),
  },
  {
    path: "/image-viewer/:document?/:image?",
    name: "image-viewer",
    meta: { requiresAuth: false, title: "Image Viewer", show_back_btn: false },
    component: () =>
      import(
        /* webpackChunkName: "ImageViewer" */ "@/components/common/ImageViewer.vue"
      ),
  },
  {
    path: "/complete-finvu-detail/:doc_id",
    name: "finvu-detail",
    meta: {
      requiresAuth: false,
      title: "Complete Finvu Detail",
      show_back_btn: false,
    },
    component: () =>
      import(
        /* webpackChunkName: "ImageViewer" */ "@/components/los/LoanDetails/CompleteFinvuDetails.vue"
      ),
  },
];

// ---- Partner portal lock ------------------------------------------------------

/**
 * Strict routing for the partner portal:
 * - a signed-in partner may only be on its own /partner/<nbfc>/<dsa> pages
 *   (the dashboard and /reports);
 *   every other URL (staff console, sign-in, another DSA) goes back there;
 * - a partner page without a partner session goes to the sign-in.
 */
function partnerLock(to) {
  const s = readPartnerSession();
  if (to.name === "partner-view") {
    // an admin's pass sets up its own session -- but never over a partner's own
    // sign-in: a link must not swap a signed-in DSA's tab into another session
    return s && !s.view_only ? partnerHome(s) : null;
  }
  if (s) {
    const own =
      ["partner-home", "partner-reports"].includes(to.name) && to.params.nbfc === s.nbfc_code && to.params.dsa === s.slug;
    return own ? null : partnerHome(s);
  }
  if (to.meta && to.meta.partnerPrivate) {
    return { name: "partner-login", params: { nbfc: to.params.nbfc } };
  }
  return null;
}

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

// ---- Access ---------------------------------------------------------------
// Routes whose permission is not simply "can view the tab in tab_title".
// A list means any-of: a detail page opened from more than one tab.
const ROUTE_PERMISSIONS = {
  "add-team": "team.edit",
  "team-details": "team.view",
  "add-dealer": "dealer.edit",
  "add-loan-scheme": "loan_schema.edit",
  "lead-details": ["los.view_details", "leads.view_details"],
  "add-lead-list": "leads.create",
  "agent-create-lead": ["leads.create", "leads.view_details"],
  "lms-details": ["lms.view_details", "collection.view_details"],
  account_disbursement: ["disbursement.view_details", "los.view_details"],
  "collection-contacts": "collection.contacts",
  "create-report-template": ["reports.template.create", "reports.template.edit"],
  "generate-report": "reports.generate",
};

function routePermission(route) {
  if (route.meta && route.meta.skip_tab_permission) return null;
  if (route.meta && route.meta.permission) return route.meta.permission;
  if (ROUTE_PERMISSIONS[route.name]) return ROUTE_PERMISSIONS[route.name];
  if (SETTINGS_ROUTE_SUBTABS[route.name]) {
    return `settings.${SETTINGS_ROUTE_SUBTABS[route.name]}.view`;
  }
  return tabViewCode(route.meta && route.meta.tab_title);
}

function canAccess(code) {
  return store.getters["access/can"](code);
}

export function firstPermittedRoute() {
  const hit = LANDING_ROUTES.find((r) => canAccess(r.code));
  return hit ? hit.name : null;
}

function firstSettingsRoute() {
  const order = [
    "settings flags",
    "settings profile",
    "settings calling",
    "settings reloan",
    "settings noc charges",
    "settings auto approval",
    "verifiaction parameters",
    "settings disbursement accounts",
    "settings partner offers",
    "settings auto assignment",
    "settings banners",
    "settings activity",
  ];
  const hit = order.find((name) =>
    canAccess(`settings.${SETTINGS_ROUTE_SUBTABS[name]}.view`)
  );
  return hit || "no-access";
}

router.beforeEach((to, from, next) => {
  const partner = partnerLock(to);
  if (partner) {
    next(partner);
    return;
  }
  if (to.meta && (to.meta.partnerPublic || to.meta.partnerPrivate)) {
    next();
    return;
  }
  // One login covers every tab, so a signed-in browser never needs the login
  // page: send it to its landing tab (logging in to another NBFC means logging
  // out first).
  if (to.name === "login" && getAuth("token")) {
    const landing = firstPermittedRoute();
    if (landing) {
      next({ name: landing });
      return;
    }
  }
  const needsAuth = to.matched.some((r) => r.meta && r.meta.requiresAuth);
  if (!needsAuth || !getAuth("token")) {
    next();
    return;
  }
  const decide = () => {
    const required = routePermission(to);
    // Access that could not be loaded (network) is left to the server, which
    // enforces the same codes, rather than locking the teammate out.
    if (!required || !store.getters["access/loaded"] || canAccess(required)) {
      next();
      return;
    }
    const landing = firstPermittedRoute();
    next({ name: landing && landing !== to.name ? landing : "no-access" });
  };
  // Once access is loaded, decide in the same tick. Waiting on a resolved
  // promise made every query-only router.replace async, so a later replace
  // (v-tabs auto-selecting its first tab) aborted the pending one and the
  // lead-detail page always opened on Internal Score.
  if (store.getters["access/loaded"]) {
    decide();
    return;
  }
  store.dispatch("access/ensureLoaded").then(decide);
});

/* redirecting to home page if access token is not provided*/
// router.beforeEach((to, from, next) => {

//     let access_token = getAuth("token")
//     // console.log(access_token);
//     if (to.meta.requiresAuth == true && !access_token) {
//         next({
//             name: 'login'
//         })
//     } else if (to.meta.requiresAuth == true && access_token) {
//         next({
//             name: 'team'
//         })
//     } else
//         next()

// });

/**
 * Opening a details page ("View details", contacts, ...) is an action for the
 * teammate's session, even though the page only reads. Only a move to another
 * page or record counts: switching the page's sub-tabs changes the query alone
 * and is not an action. Tab (list) routes need a `.view` code, which never counts.
 */
function noteDetailOpened(to, from) {
  const code = ROUTE_PERMISSIONS[to.name];
  if (!code) return;
  const sameRecord =
    from &&
    to.name === from.name &&
    JSON.stringify(to.params || {}) === JSON.stringify(from.params || {});
  if (!sameRecord) noteAction(code);
}

const guard = function (to, from, next) {
  if (getAuth("token")) {
    next();
  } else if (getAuth("token") == "" && next("/")) {
    next("/");
  } else {
    next("/");
  }
};

// Section list routes. When the user lands on one of these we remember it so a
// later search from a detail page returns to the section the user started in,
// even if that detail page belongs to a different section (e.g. the Collection
// list opens an LMS-details page).
const LIST_ROUTE_NAMES = [
  "lms-List",
  "lead-list",
  "agent-los",
  "nach-list",
  "account-list",
  "collection-list",
  "notice-management",
  "session_history",
  "ledger-panel",
  "payments-panel",
  "reports-panel",
  "notifications",
  "team",
  "loan-scheme",
  "dealer",
  "delete-requests",
  "dashboard",
];

// Keep the borrower-comments widget in sync with the active route.
// LMS routes carry `:mobile` directly — LOS detail pages set it explicitly
// from their loaded borrower payload (router has no view into that).
router.afterEach((to, from) => {
  if (to && LIST_ROUTE_NAMES.includes(to.name)) {
    store.dispatch("setLastListRoute", to.name);
  }
  if (!getAuth("token")) {
    store.commit("comments/CLEAR_ACTIVE_MOBILE");
    return;
  }
  noteDetailOpened(to, from);
  const mobile =
    (to && to.params && to.params.mobile) ||
    (to && to.query && to.query.mobile);
  if (mobile) {
    store.commit("comments/SET_ACTIVE_MOBILE", mobile);
    return;
  }
  // A query-only change is still the same record. The LOS step tabs navigate via
  // `$router.replace({ query: { tab } })`, so clearing unconditionally here wiped
  // the active consumer on every tab click — and because the comments launcher
  // renders on `v-if="activeMobile"`, the button itself vanished and only came
  // back by reloading the page. Only a genuine move to another page or record
  // should forget the consumer.
  const sameRecord =
    from &&
    to.name === from.name &&
    JSON.stringify(to.params || {}) === JSON.stringify(from.params || {});
  if (sameRecord) return;

  store.commit("comments/CLEAR_ACTIVE_MOBILE");
});

// router.beforeEach((to, from, next) => {

//     let access_token = getAuth("token")
//     if (to.meta.requiresAuth == true && !access_token) {
//         next({
//             name: 'login'
//         })
//     } else if (to.meta.requiresAuth == false && access_token) {
//         next({
//             name: 'team'
//         })
//     } else
//         next()

// });

export default router;
